import { fetchAuthSession } from '@aws-amplify/auth';
import { checkSessionInvalid } from './apiInterceptor';

/**
 * Safari 16 API Compatibility Layer
 *
 * Safari 16.0 has a bug where aws-amplify/api throws "InvalidApiName: API name is invalid"
 * even when APIs are properly configured. This service detects Safari 16.0 and provides
 * a native fetch() fallback while maintaining the same interface as ChiroUpAPI.
 *
 * Usage:
 *   Safari16API.configure({
 *     api: 'https://api.example.com',
 *     'api-noauth': 'https://api.example.com'
 *   });
 *
 *   const data = await Safari16API.get('api', '/endpoint', {});
 */

// Detect Safari 16.x or lower (all versions with Amplify bugs)
const isSafari16 = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const match = ua.match(/Version\/([\d.]+)/);
  if (!match) return false;

  // Parse major version (e.g., "16.6.1" -> 16)
  const versionParts = match[1].split('.');
  const majorVersion = parseInt(versionParts[0], 10);

  // Use fallback for Safari 16.x and below
  return majorVersion <= 16;
})();

// API endpoint registry
const apiRegistry: Record<string, string> = {};

// Optional custom resolver for the Authorization header value. Apps that
// don't use Cognito (e.g. healthcom-schedule, which authenticates with its
// own session tokens) can override how the auth header is built so the
// Safari <=16 fallback matches their Amplify REST `headers` callback instead
// of calling Cognito's `fetchAuthSession()`.
type AuthHeaderResolver = (apiName: string) => Promise<string | undefined> | string | undefined;
let authHeaderResolver: AuthHeaderResolver | undefined;

/**
 * Configure API endpoints
 * @param apis - Object mapping API names to endpoints
 * @param options - Optional overrides (e.g. a custom Authorization resolver)
 */
const configure = (
  apis: Record<string, string>,
  options?: { resolveAuthHeader?: AuthHeaderResolver }
) => {
  Object.assign(apiRegistry, apis);
  if (options?.resolveAuthHeader) {
    authHeaderResolver = options.resolveAuthHeader;
  }
};

/**
 * Get endpoint for API name
 */
const getEndpoint = (apiName: string): string => {
  const endpoint = apiRegistry[apiName];

  if (!endpoint) {
    throw new Error(`API endpoint not configured for: ${apiName}`);
  }

  return endpoint;
};

/**
 * Native fetch implementation for Safari 16
 */
const fetchFallback = async <T = any>(
  apiName: string,
  path: string,
  method: string,
  options: any = {}
): Promise<T> => {
  const endpoint = getEndpoint(apiName);
  // Join endpoint + path preserving the endpoint's own path segment (e.g. the
  // API Gateway stage in a raw `.../prod` execute-api URL). `new URL(path,
  // endpoint)` treats a leading-slash path as root-relative and DISCARDS that
  // segment, so `new URL('/kiosk/me', 'https://host/prod')` → 'https://host/kiosk/me'
  // (no stage) → 403 with no CORS headers, surfacing as a bogus CORS error.
  // aws-amplify/api concatenates instead, which is why only the Safari <=16 path broke.
  const url = new URL(`${endpoint.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`);

  // Add query params
  if (options.queryParams) {
    Object.entries(options.queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  // Build headers
  const headers: Record<string, string> = {};

  // Add Content-Type for requests with body
  if (options.body && !options.headers?.['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Add auth header for 'api' endpoint (not for 'api-noauth')
  if (apiName === 'api') {
    if (authHeaderResolver) {
      // App provided its own auth scheme (non-Cognito session tokens).
      try {
        const value = await authHeaderResolver(apiName);
        if (value) {
          headers['Authorization'] = value;
        }
      } catch (authError) {
        console.warn('[Safari16API] Custom auth resolver failed:', authError);
      }
    } else {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (authError) {
        console.warn('[Safari16API] Could not get auth session:', authError);
      }
    }
  }

  // Merge with any custom headers
  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (options.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(url.toString(), fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: response.statusText,
    }));
    // Funnel session-death responses through the same interceptor ChiroUpAPI
    // uses, so iOS / Safari <=16 users get auto-signed-out instead of stuck
    // on a spinner.
    checkSessionInvalid(errorData, response.status);
    // Throw in same format as ChiroUpAPI for consistency
    throw {
      response: {
        data: {
          statusCode: response.status,
          ...errorData,
        },
      },
    };
  }

  // Handle different response types
  if (options.responseType === 'blob') {
    return response as unknown as T;
  }

  const json = await response.json().catch(() => null);

  return json as T;
};

/**
 * GET request - Always uses fetch fallback
 * Detection happens in ChiroUpAPI, so if we're here, we know we need the fallback
 */
const get = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'GET', options);
};

/**
 * POST request - Always uses fetch fallback
 */
const post = async <T = any>(
  apiName: any,
  path: any,
  options: any = {},
  requestOptions: { type?: 'json' | 'blob' | 'text' } = {}
): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'POST', options);
};

/**
 * PUT request - Always uses fetch fallback
 */
const put = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'PUT', options);
};

/**
 * PATCH request - Always uses fetch fallback
 */
const patch = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'PATCH', options);
};

/**
 * DELETE request - Always uses fetch fallback
 */
const del = async <T = any>(apiName: any, path: any, options: any = {}): Promise<T> => {
  return fetchFallback<T>(apiName, path, 'DELETE', options);
};

export const Safari16API = {
  configure,
  get,
  post,
  put,
  patch,
  del,
  isSafari16,
};
